import React, { createContext, useContext, useRef } from "react";
import { toast } from "react-toastify";
import axiosInstance from "../Services/axiosInstance";
import { handleFetchPredictors } from "../modules/predictorModule.ts";
import { handleFetchDatasets } from "../modules/datasetModule.ts";

interface SSEContextType {
    subscribeJob: (jobType: string, jobId: number) => void;
}

const SSEContext = createContext<SSEContextType | null>(null);

export const SSEProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const sources = useRef<Record<string, AbortController>>({});

    const fetchPredictorDetails = async (predictorId: number) => {
        try {
            const response = await handleFetchPredictors(0, 10, 1);
            const predictor = response.data.find(pred => pred.id === predictorId);
            return predictor || null;
        } catch (error) {
            console.error('Failed to fetch predictor details:', error);
            return null;
        }
    };

    const fetchStatistics = async (datasetId: number) => {
        try {
            const response = await handleFetchDatasets(0, 10, 1)
            const dataset = response.data.find(data => data.id === datasetId);
            return dataset || null;
        } catch (error) {
            console.error('Failed to fetch Dataset details:', error);
            return null;
        }
    }

    const getNavigationUrl = (jobType: string, jobId: number): string => {
        switch (jobType) {
            case "Training":
                return `/modal-details/${jobId}`;
            // case "Prediction":
            //     return `/prediction-details/${jobId}`;
            case "statistics":
                return `/dataset-view/${jobId}`;
            default:
                return `/job-details/${jobId}`;
        }
    };

    const subscribeJob = (jobType: string, jobId: number) => {
        const key = `${jobType}-${jobId}`;

        if (sources.current[key]) return;

        const controller = new AbortController();
        sources.current[key] = controller;

        const token = sessionStorage.getItem("access_token");
        const url = `https://data-api.sandbox.blu-ocean.net/api/v1/train/events/${jobType}/${jobId}`;

        const parseEvent = (line: string) => {
        const eventMatch = line.match(/^event: (.+)$/m);
        const dataMatch = line.match(/^data: (.+)$/m);

        if (!eventMatch || !dataMatch) return null;

        return {
            type: eventMatch[1],
            data: dataMatch[1],
        };
        };

        const getToastMessage = async (
        data: any,
        jobType: string,
        jobId: string
        ) => {
        let message = `${data.job_type} #${data.job_id} completed`;

        switch (jobType || data.job_type) {
            case "Training": {
            const details = await fetchPredictorDetails(jobId);
            const name = details?.predictor_name || `Predictor #${jobId}`;
            return `Training for ${name} has been completed.`;
            }

            case "statistics": {
            const details = await fetchStatistics(jobId);
            const name = details?.dataset_name || `Dataset #${jobId}`;
            return `Statistics Generation for ${name} has been completed.`;
            }

            case "batch_prediction":
            return `Batch Prediction for ${jobId} has been completed.`;

            case "shap": {
            const details = await fetchPredictorDetails(jobId);
            const name = details?.predictor_name || `Predictor #${jobId}`;
            return `Feature Importance Generation for ${name} has been completed.`;
            }

            default:
            return message;
        }
        };

        const showCompletionToast = (message: string) => {
        const ToastContent = () => (
            <div style={{ display: "flex", justifyContent: "space-between", width: "100%" }}>
            <span>{message}</span>
            </div>
        );

        toast.success(<ToastContent />, {
            position: "top-center",
            autoClose: false,
            closeButton: true,
            closeOnClick: false,
            draggable: false,
        });
        };

        const handleCompletedEvent = async (
        eventData: string,
        jobType: string,
        jobId: string,
        controller: AbortController,
        key: string
        ) => {
        const data = JSON.parse(eventData);
        const message = await getToastMessage(data, jobType, jobId);

        showCompletionToast(message);

        controller.abort();
        delete sources.current[key];

        window.dispatchEvent(
            new CustomEvent("job-completed", { detail: data })
        );
        };

        const processStreamChunk = async (
        chunk: string,
        jobType: string,
        jobId: string,
        controller: AbortController,
        key: string
        ) => {
        const lines = chunk.split("\n\n");

        for (const line of lines) {
            if (!line.trim()) continue;

            const parsed = parseEvent(line);
            if (!parsed) continue;

            if (parsed.type === "completed") {
            await handleCompletedEvent(
                parsed.data,
                jobType,
                jobId,
                controller,
                key
            );
            return true;
            }
        }

        return false;
        };

        const connectSSE = async () => {
        try {
            const response = await fetch(url, {
            headers: {
                Authorization: `Bearer ${token}`,
                Accept: "text/event-stream",
            },
            signal: controller.signal,
            });

            if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
            }

            const reader = response.body?.getReader();
            if (!reader) return;

            const decoder = new TextDecoder();
            let buffer = "";

            while (true) {
            const { done, value } = await reader.read();
            if (done) break;

            buffer += decoder.decode(value, { stream: true });

            const parts = buffer.split("\n\n");
            buffer = parts.pop() || "";

            const isCompleted = await processStreamChunk(
                parts.join("\n\n"),
                jobType,
                jobId,
                controller,
                key
            );

            if (isCompleted) return;
            }
        } catch (error: any) {
            if (error.name !== "AbortError") {
            console.error("SSE error:", error);
            }
            delete sources.current[key];
        }
        };

        connectSSE();
    };

    return (
        <SSEContext.Provider value={{ subscribeJob }}>
            {children}
        </SSEContext.Provider>
    );
};

export const useSSE = () => {
    const ctx = useContext(SSEContext);
    if (!ctx) throw new Error("Missing SSEProvider");
    return ctx;
};